import {
  ArrowDropDown,
  ArrowDropUp,
  ExpandLess,
  KeyboardArrowRight,
  MoreVertRounded,
  ViewWeekOutlined,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  CSSProperties,
  IconButton,
  Menu,
  MenuItem,
  PopoverPosition,
  Stack,
  styled,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Column,
  ColumnDef,
  ColumnPinningState,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  Row,
  RowData,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import React, { useEffect, useRef, useState } from "react";
import { TableColumnsMenu, useTableSettings } from "src/components";

const TableContainer = styled(Box)`
  background-color: #fff;
  position: relative;
  overflow-x: auto;
  width: 100%;
`;

const Table = styled("table")<{ $hasSubrows: boolean }>`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  th {
    height: 30px;
    padding: 5px 10px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    border-bottom: 1px solid ${({ theme }) => theme.palette.line.main};
    position: sticky;
    top: 0;
    z-index: 2;

    &:first-child {
      padding-left: ${({ $hasSubrows }) => ($hasSubrows ? "50px" : "20px")};
    }

    .columnMenuButton {
      opacity: 0;
    }

    &:hover .columnMenuButton {
      opacity: 1;
    }
  }

  tbody tr {
    &:last-child td {
      border-bottom: none;
    }

    &:hover {
      background-color: ${({ theme }) => theme.palette.primary.opacity5};
    }

    &:hover td.pinned {
      background: ${({ theme }) => theme.palette.primary.opacity5};
    }

    &:hover td.pinned-last-left {
      background: linear-gradient(90deg, ${({ theme }) => theme.palette.primary.opacity5} 80%, transparent 100%);
    }

    &:hover td.pinned-first-right {
      background: linear-gradient(90deg, transparent 0%, ${({ theme }) => theme.palette.primary.opacity5} 20%);
    }
  }

  td {
    padding: 5px 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-bottom: 1px dashed ${({ theme }) => theme.palette.line.main};

    .drawerButton {
      position: absolute;
      right: 0;
      opacity: 0;
    }

    &:hover .drawerButton {
      opacity: 1;
    }
  }

  th.pinned {
    z-index: 3;
  }

  td.pinned {
    z-index: 2;
  }

  th.pinned,
  td.pinned {
    background: #ffffff;
  }

  td.pinned-last-left {
    background: linear-gradient(90deg, #ffffff 80%, transparent 100%);
  }

  td.pinned-first-right {
    background: linear-gradient(90deg, transparent 0%, #ffffff 20%);
  }
`;

const getColumnPinningClassNames = <TData extends RowData>(column: Column<TData>): string => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn = isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn = isPinned === "right" && column.getIsFirstColumn("right");

  const classNames = [];

  if (isPinned) {
    classNames.push(`pinned pinned-${isPinned}`);
  }

  if (isLastLeftPinnedColumn) {
    classNames.push("pinned-last-left");
  }

  if (isFirstRightPinnedColumn) {
    classNames.push("pinned-first-right");
  }

  return classNames.join(" ");
};

const getColumnPinningStyles = <TData extends RowData>(column: Column<TData>): CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    position: isPinned ? "sticky" : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    width: column.getSize(),
  };
};

const ColumnResizer = styled("div")<{ $isResizing: boolean }>`
  position: absolute;
  top: 0;
  right: 2px;
  height: 40px;
  width: 10px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  display: flex;
  align-items: center;
  justify-content: end;

  &:after {
    content: "";
    width: 2px;
    background-color: ${({ theme }) => theme.palette.grey[100]};
    height: 20px;
  }

  &:hover {
    &:after {
      background-color: ${({ theme }) => theme.palette.primary.main};
    }
  }

  ${({ theme, $isResizing }) =>
    $isResizing &&
    `
    &:after {
    background-color: ${theme.palette.primary.main};
    }
  `}
`;

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    hideResizer?: boolean;
    hideColumnMenu?: boolean;
  }
}

interface Props<T> {
  tableId: string;
  data: T[];
  placeholder?: React.ReactNode;
  columns: ColumnDef<T, any>[];
  columnPinning?: ColumnPinningState;
  columnVisibility?: Record<string, boolean>;
  enableSorting?: boolean;
  sorting?: SortingState;
  getRowCanExpand?: (row: Row<T>) => boolean;
  getSubrows?: (originalRow: T, index: number) => T[] | undefined;
  onExpandRowClicked?: (
    row: Row<T>,
    isLoadingSubRows: boolean,
    setIsLoadingSubRows: (isLoading: boolean) => void,
  ) => void;
  sx?: SxProps;
}

const TableComponent = <T extends object>(props: Props<T>) => {
  const [isLoadingSubRows, setIsLoadingSubRows] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [sorting, setSorting] = useState<SortingState>(props.sorting ?? []);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [columnsMenuPosition, setColumnsMenuPosition] = useState<PopoverPosition | undefined>(undefined);
  const { settings } = useTableSettings(props.tableId);
  const theme = useTheme();

  const table = useReactTable<T>({
    data: props.data,
    columns: props.columns,
    defaultColumn: {
      size: 150,
      minSize: 150,
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      expanded,
      sorting,
    },
    onExpandedChange: setExpanded,
    getRowCanExpand: props.getRowCanExpand,
    getSubRows: props.getSubrows,
    onSortingChange: setSorting,
    enableSorting: props.enableSorting,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    initialState: {
      columnPinning: {
        ...props.columnPinning,
      },
      columnVisibility: {
        ...settings.columnVisibility,
        ...props.columnVisibility,
      },
    },
  });

  const tableContainerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    table.setColumnVisibility({
      ...settings.columnVisibility,
      ...props.columnVisibility,
    });
  }, [props.columnVisibility, settings.columnVisibility, table]);

  function renderPlaceholder() {
    if (props.placeholder) {
      return props.placeholder;
    }

    return <Typography variant="placeholder">Nothing here yet.</Typography>;
  }

  return (
    <>
      <TableContainer ref={tableContainerRef} sx={{ ...props.sx }}>
        {props.data.length === 0 ? (
          renderPlaceholder()
        ) : (
          <Table
            $hasSubrows={props.getSubrows !== undefined}
            style={{ width: `max(${table.getCenterTotalSize()}px, 100%)` }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <React.Fragment key={header.id}>
                      {/* Add a flexible column before the last column or before the pinned-right columns */}
                      {(table.getIsSomeColumnsPinned("right")
                        ? index === headerGroup.headers.length - table.getRightVisibleLeafColumns().length
                        : index === headerGroup.headers.length - 1) && (
                        <th key={`spacer-${header.id}`} style={{ width: "auto", maxWidth: 0 }}></th>
                      )}

                      <th
                        key={header.id}
                        className={getColumnPinningClassNames(header.column)}
                        style={
                          {
                            width: `max(${header.column.columnDef.minSize}px, ${header.column.getSize()}px)`,
                            cursor: header.column.getCanSort() ? "pointer" : undefined,
                            ...getColumnPinningStyles(header.column),
                          } as React.CSSProperties
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Stack
                          direction="row"
                          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}

                          {header.column.getCanSort() && (
                            <>
                              {header.column.getIsSorted() === "asc" ? (
                                <ArrowDropUp />
                              ) : header.column.getIsSorted() === "desc" ? (
                                <ArrowDropDown />
                              ) : (
                                <ArrowDropUp
                                  sx={{
                                    opacity: 0,
                                    "&:hover": {
                                      opacity: 1,
                                    },
                                  }}
                                />
                              )}
                            </>
                          )}

                          {!header.column.columnDef.meta?.hideColumnMenu && (
                            <IconButton
                              className="columnMenuButton"
                              size="small"
                              onClick={(e) => setMenuAnchor(e.currentTarget)}
                              sx={{ mr: 0.5 }}
                            >
                              <MoreVertRounded />
                            </IconButton>
                          )}
                        </Stack>

                        {!header.column.columnDef.meta?.hideResizer && header.column.getCanResize() && (
                          <ColumnResizer
                            onDoubleClick={() => header.column.resetSize()}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            $isResizing={header.column.getIsResizing()}
                          />
                        )}
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index, cells) => (
                    <React.Fragment key={cell.id}>
                      {/* Add a flexible column before the last column or before the pinned-right columns */}
                      {(table.getIsSomeColumnsPinned("right")
                        ? index === cells.length - table.getRightVisibleLeafColumns().length
                        : index === cells.length - 1) && <td key={`spacer-${row.id}`}></td>}
                      <td
                        key={cell.id}
                        className={getColumnPinningClassNames(cell.column)}
                        style={
                          {
                            width: `${cell.column.getSize()}px`,
                            ...getColumnPinningStyles(cell.column),
                          } as React.CSSProperties
                        }
                      >
                        {index === 0 ? (
                          <Box sx={{ pl: row.depth, position: "relative" }}>
                            {row.getCanExpand() && (
                              <IconButton
                                size="small"
                                disabled={isLoadingSubRows[row.id]}
                                onClick={() =>
                                  props.onExpandRowClicked?.(row, isLoadingSubRows[row.id] ?? false, (isLoading) => {
                                    setIsLoadingSubRows({ [row.id]: isLoading });
                                  })
                                }
                                sx={{ position: "absolute", left: (row.depth - 1) * 20 + 25, top: 0 }}
                              >
                                {row.getIsExpanded() ? (
                                  <ExpandLess />
                                ) : isLoadingSubRows[row.id] ? (
                                  <CircularProgress size={15} sx={{ color: theme.palette.text.disabled }} />
                                ) : (
                                  <KeyboardArrowRight />
                                )}
                              </IconButton>
                            )}

                            <Box sx={{ pl: props.getRowCanExpand ? 1.75 : 0.25 }}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </Box>
                          </Box>
                        ) : (
                          flexRender(cell.column.columnDef.cell, cell.getContext())
                        )}
                      </td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableContainer>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={(e) => {
            setMenuAnchor(null);
            setColumnsMenuPosition({
              top: e.clientY,
              left: e.clientX,
            });
          }}
        >
          <Stack direction="row" spacing={0.25} sx={{ alignItems: "center" }}>
            <ViewWeekOutlined />
            <Typography>Columns</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      <TableColumnsMenu
        tableId={props.tableId}
        table={table}
        anchorPosition={columnsMenuPosition}
        onClose={() => setColumnsMenuPosition(undefined)}
      />
    </>
  );
};

export { TableComponent as Table };
